import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Collapse,
  IconButton
} from '@mui/material';
import {
  TrendingUp,
  TouchApp,
  AttachMoney,
  Assessment,
  FileDownload,
  Article,
  Visibility,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';

const StatCard = ({ title, value, icon, color = 'primary' }) => (
  <Card elevation={2}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="overline">
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {value}
          </Typography>
        </Box>
        <Box sx={{ color: `${color}.main` }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const ReportsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [stats, setStats] = useState(null);
  const [blogStats, setBlogStats] = useState(null);
  const [dateRange, setDateRange] = useState('30'); // days
  const [loading, setLoading] = useState(true);
  const [blogLoading, setBlogLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedBlogs, setExpandedBlogs] = useState({}); // Track which blogs are expanded
  const [expandedAffiliates, setExpandedAffiliates] = useState({}); // Track which affiliates are expanded (admin only)
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [snakeStats, setSnakeStats] = useState(null);
  const [snakeLoading, setSnakeLoading] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState('');
  const [viewAllAffiliates, setViewAllAffiliates] = useState(false);
  
  // Feature flag: Show unique sessions column
  const SHOW_UNIQUE_SESSIONS = false; // Set to true to show unique sessions column
  
  // Feature flag: Show offer/click stats sections (between date range and blog performance)
  const SHOW_OFFER_STATS = false; // Set to true to show offer/clicks/conversion stats sections
  
  // Feature flag: Show export CSV button
  const SHOW_EXPORT_CSV = false; // Set to true to show export CSV button

  const dateRanges = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: '365', label: 'Last year' }
  ];

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        if (user?.id) {
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - parseInt(dateRange));

          const statsData = await apiService.getAffiliateStats(user.id, {
            start_date: startDate.toISOString(),
            end_date: endDate.toISOString()
          });
          setStats(statsData);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        setError('Failed to load reports data');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user, dateRange]);

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await apiService.getCategoriesForSelect();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Load affiliates for admin
  useEffect(() => {
    const loadAffiliates = async () => {
      if (isAdmin) {
        try {
          const affiliatesData = await apiService.getAllAffiliates();
          setAffiliates(affiliatesData);
          // Set default to view all if admin
          setViewAllAffiliates(true);
        } catch (error) {
          console.error('Error loading affiliates:', error);
        }
      }
    };
    loadAffiliates();
  }, [isAdmin]);

  useEffect(() => {
    const fetchBlogStats = async () => {
      setBlogLoading(true);
      try {
        // Determine which affiliate ID to use
        const targetAffiliateId = isAdmin && !viewAllAffiliates && selectedAffiliateId 
          ? selectedAffiliateId 
          : (isAdmin && viewAllAffiliates ? 'all' : user?.id);
        
        if (!targetAffiliateId && !isAdmin) {
          setBlogLoading(false);
          return;
        }

        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - parseInt(dateRange));

        console.log('Fetching blog performance stats for affiliate:', targetAffiliateId);
        console.log('Date range:', { startDate: startDate.toISOString(), endDate: endDate.toISOString() });

        const params = {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        };
        
        if (selectedCategory) {
          params.categoryId = selectedCategory;
        }
        if (selectedSubcategory) {
          params.subcategoryId = selectedSubcategory;
        }

        // Add viewAll parameter for admin
        if (isAdmin && viewAllAffiliates) {
          params.viewAll = 'true';
        }

        // Use a placeholder ID if viewing all (backend will handle it)
        const affiliateIdForRequest = viewAllAffiliates && isAdmin ? 'all' : targetAffiliateId;
        const blogStatsData = await apiService.getBlogPerformanceStats(affiliateIdForRequest, params);
          
        console.log('Blog performance stats response:', blogStatsData);
        console.log('Blog stats structure:', {
          success: blogStatsData?.success,
          hasStats: !!blogStatsData?.stats,
          overall: blogStatsData?.stats?.overall,
          head: blogStatsData?.stats?.head,
          snake: blogStatsData?.stats?.snake,
          byBlog: blogStatsData?.stats?.byBlog,
          byBlogLength: blogStatsData?.stats?.byBlog?.length,
          pageStatsByBlog: blogStatsData?.stats?.pageStatsByBlog,
          pageStatsByBlogLength: blogStatsData?.stats?.pageStatsByBlog?.length
        });
          
        setBlogStats(blogStatsData?.stats || null);
      } catch (error) {
        console.error('Error fetching blog stats:', error);
        setError('Failed to load blog performance data');
          setBlogStats(null);
      } finally {
        setBlogLoading(false);
      }
    };

    fetchBlogStats();
  }, [user, dateRange, selectedCategory, selectedSubcategory, isAdmin, selectedAffiliateId, viewAllAffiliates]);

  // Fetch Snake Stats (Admin only)
  useEffect(() => {
    // Skip fetching snake stats for non-admin users
    if (!isAdmin) {
      setSnakeStats(null);
      return;
    }

    const fetchSnakeStats = async () => {
      setSnakeLoading(true);
      try {
        // Determine which affiliate ID to use
        const targetAffiliateId = !viewAllAffiliates && selectedAffiliateId 
          ? selectedAffiliateId 
          : (viewAllAffiliates ? 'all' : user?.id);
        
        if (!targetAffiliateId) {
          setSnakeLoading(false);
          return;
        }

        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - parseInt(dateRange));

        const params = {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        };
        
        if (selectedCategory) {
          params.categoryId = selectedCategory;
        }
        if (selectedSubcategory) {
          params.subcategoryId = selectedSubcategory;
        }

        // Add viewAll parameter for admin
        if (viewAllAffiliates) {
          params.viewAll = 'true';
        }

        // Use a placeholder ID if viewing all (backend will handle it)
        const affiliateIdForRequest = viewAllAffiliates ? 'all' : targetAffiliateId;
        const snakeStatsData = await apiService.getSnakeStats(affiliateIdForRequest, params);
        setSnakeStats(snakeStatsData?.stats || null);
      } catch (error) {
        console.error('Error fetching snake stats:', error);
        setSnakeStats(null);
      } finally {
        setSnakeLoading(false);
      }
    };

    fetchSnakeStats();
  }, [user, dateRange, selectedCategory, selectedSubcategory, isAdmin, selectedAffiliateId, viewAllAffiliates]);

  const handleToggleBlog = (blogPostId) => {
    setExpandedBlogs(prev => ({
      ...prev,
      [blogPostId]: !prev[blogPostId]
    }));
  };

  const handleToggleAffiliate = (affiliateId) => {
    setExpandedAffiliates(prev => ({
      ...prev,
      [affiliateId]: !prev[affiliateId]
    }));
  };

  const calculateTotals = (statsData) => {
    if (!statsData) return { clicks: 0, conversions: 0, revenue: 0, conversionRate: 0 };

    const clicks = statsData.clickStats?.reduce((total, stat) => total + stat.count, 0) || 0;
    const conversions = statsData.clickStats?.find(stat => stat._id === 'converted')?.count || 0;
    const revenue = statsData.conversionStats?.reduce((total, stat) => total + stat.totalPayout, 0) || 0;
    const conversionRate = clicks > 0 ? ((conversions / clicks) * 100).toFixed(2) : 0;

    return { clicks, conversions, revenue, conversionRate };
  };

  const exportData = () => {
    if (!stats) return;

    const csvData = [
      ['Metric', 'Value'],
      ['Total Clicks', calculateTotals(stats).clicks],
      ['Total Conversions', calculateTotals(stats).conversions],
      ['Total Revenue', `$${calculateTotals(stats).revenue.toFixed(2)}`],
      ['Conversion Rate', `${calculateTotals(stats).conversionRate}%`],
      [''],
      ['Top Offers', ''],
      ['Offer Name', 'Clicks', 'Conversions', 'Revenue', 'CR%'],
      ...(stats.topOffers?.map(offer => [
        offer.offerTitle,
        offer.clicks,
        offer.conversions,
        `$${offer.totalPayout.toFixed(2)}`,
        `${offer.conversionRate.toFixed(1)}%`
      ]) || [])
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `affiliate-report-${dateRange}days.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const totals = calculateTotals(stats);

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Performance Reports
        </Typography>
        {SHOW_EXPORT_CSV && (
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={exportData}
            disabled={!stats}
          >
            Export CSV
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {isAdmin && (
            <>
              <Grid item>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>View Mode</InputLabel>
                  <Select
                    value={viewAllAffiliates ? 'all' : 'single'}
                    label="View Mode"
                    onChange={(e) => {
                      const viewAll = e.target.value === 'all';
                      setViewAllAffiliates(viewAll);
                      if (!viewAll) {
                        setSelectedAffiliateId('');
                      }
                    }}
                  >
                    <MenuItem value="all">All Affiliates</MenuItem>
                    <MenuItem value="single">Single Affiliate</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {!viewAllAffiliates && (
                <Grid item>
                  <FormControl sx={{ minWidth: 250 }}>
                    <InputLabel>Affiliate</InputLabel>
                    <Select
                      value={selectedAffiliateId}
                      label="Affiliate"
                      onChange={(e) => setSelectedAffiliateId(e.target.value)}
                    >
                      {affiliates.map((affiliate) => (
                        <MenuItem key={affiliate._id} value={affiliate._id}>
                          {affiliate.name || affiliate.email} {affiliate.affiliateData?.name ? `(${affiliate.affiliateData.name})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </>
          )}
          <Grid item>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => setDateRange(e.target.value)}
              >
                {dateRanges.map((range) => (
                  <MenuItem key={range.value} value={range.value}>
                    {range.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedSubcategory(''); // Reset subcategory when category changes
                }}
              >
                <MenuItem value="">
                  <em>All Categories</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 200 }} disabled={!selectedCategory}>
              <InputLabel>Subcategory</InputLabel>
              <Select
                value={selectedSubcategory}
                label="Subcategory"
                onChange={(e) => setSelectedSubcategory(e.target.value)}
              >
                <MenuItem value="">
                  <em>All Subcategories</em>
                </MenuItem>
                {selectedCategory && categories.find(c => c._id === selectedCategory)?.subcategories?.map((sub) => (
                  <MenuItem key={sub._id} value={sub._id}>
                    {sub.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {(selectedCategory || selectedSubcategory) && (
            <Grid item>
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedCategory('');
                  setSelectedSubcategory('');
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {/* Overview Stats */}
        {SHOW_OFFER_STATS && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Clicks"
                value={totals.clicks.toLocaleString()}
                icon={<TouchApp fontSize="large" />}
                color="primary"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Conversions"
                value={totals.conversions.toLocaleString()}
                icon={<TrendingUp fontSize="large" />}
                color="success"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Revenue"
                value={`$${totals.revenue.toFixed(2)}`}
                icon={<AttachMoney fontSize="large" />}
                color="warning"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Conversion Rate"
                value={`${totals.conversionRate}%`}
                icon={<Assessment fontSize="large" />}
                color="info"
              />
            </Grid>

            {/* Conversion Events Breakdown */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Conversion Events
                </Typography>
                {stats?.conversionStats && stats.conversionStats.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Event Type</TableCell>
                          <TableCell align="right">Count</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.conversionStats.map((event) => (
                          <TableRow key={event._id}>
                            <TableCell component="th" scope="row">
                              {event._id.charAt(0).toUpperCase() + event._id.slice(1)}
                            </TableCell>
                            <TableCell align="right">{event.count}</TableCell>
                            <TableCell align="right">${event.totalPayout.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No conversion events in this period
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Click Status Breakdown */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Click Status
                </Typography>
                {stats?.clickStats && stats.clickStats.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Count</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.clickStats.map((click) => (
                          <TableRow key={click._id}>
                            <TableCell component="th" scope="row">
                              {click._id.charAt(0).toUpperCase() + click._id.slice(1)}
                            </TableCell>
                            <TableCell align="right">{click.count}</TableCell>
                            <TableCell align="right">${click.totalPayout.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No click data in this period
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Top Offers Table */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Top Performing Offers
                </Typography>
                {stats?.topOffers && stats.topOffers.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Offer Name</TableCell>
                          <TableCell align="right">Clicks</TableCell>
                          <TableCell align="right">Conversions</TableCell>
                          <TableCell align="right">Conversion Rate</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.topOffers.map((offer) => (
                          <TableRow key={offer.offerId}>
                            <TableCell component="th" scope="row">
                              {offer.offerTitle}
                            </TableCell>
                            <TableCell align="right">{offer.clicks}</TableCell>
                            <TableCell align="right">{offer.conversions}</TableCell>
                            <TableCell align="right">{offer.conversionRate.toFixed(1)}%</TableCell>
                            <TableCell align="right">${offer.totalPayout.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No offers data available for this period
                  </Typography>
                )}
              </Paper>
            </Grid>
          </>
        )}

        {/* Blog Performance Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Article color="primary" />
              <Typography variant="h6">
                Blog Performance
              </Typography>
            </Box>
            
            {blogLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : blogStats ? (
              <>
                {/* Overall Blog Stats */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={1}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography color="textSecondary" gutterBottom variant="overline">
                              Total Blog Views
                            </Typography>
                            <Typography variant="h5" component="div">
                              {(blogStats.overall?.totalViews || 0).toLocaleString()}
                            </Typography>
                          </Box>
                          <Visibility color="primary" />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={1}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography color="textSecondary" gutterBottom variant="overline">
                              Sales
                            </Typography>
                            <Typography variant="h5" component="div">
                              {(blogStats.overall?.sales || 0).toLocaleString()}
                            </Typography>
                          </Box>
                          <TrendingUp color="success" />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={1}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography color="textSecondary" gutterBottom variant="overline">
                              Revenue
                            </Typography>
                            <Typography variant="h5" component="div">
                              ${(blogStats.overall?.revenue || 0).toFixed(2)}
                            </Typography>
                          </Box>
                          <AttachMoney color="warning" />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={1}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography color="textSecondary" gutterBottom variant="overline">
                              Unique Sessions
                            </Typography>
                            <Typography variant="h5" component="div">
                              {(blogStats.overall?.uniqueSessions || 0).toLocaleString()}
                            </Typography>
                          </Box>
                          <Assessment color="info" />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Helper function to render blog table */}
                {(() => {
                  const renderBlogTable = (blogs, pageStatsArray, sectionTitle, sectionTotals) => {
                    if (!blogs || blogs.length === 0) return null;
                    
                    // Group blogs by affiliate for admin users and calculate affiliate totals
                    let blogsToRender = blogs;
                    let affiliateGroups = [];
                    if (isAdmin && viewAllAffiliates) {
                      // Group by affiliate
                      const groupedByAffiliate = new Map();
                      blogs.forEach(blog => {
                        const affiliateId = blog.affiliateId || 'unknown';
                        if (!groupedByAffiliate.has(affiliateId)) {
                          groupedByAffiliate.set(affiliateId, {
                            affiliateId: blog.affiliateId,
                            affiliateName: blog.affiliateName,
                            affiliateUsername: blog.affiliateUsername,
                            blogs: [],
                            totalViews: 0,
                            totalSales: 0,
                            totalRevenue: 0
                          });
                        }
                        const group = groupedByAffiliate.get(affiliateId);
                        group.blogs.push(blog);
                        group.totalViews += blog.totalViews || 0;
                        group.totalSales += blog.sales || 0;
                        group.totalRevenue += blog.totalRevenue || 0;
                      });
                      
                      // Convert to array and sort by affiliate name
                      affiliateGroups = Array.from(groupedByAffiliate.values()).sort((a, b) => {
                        const nameA = (a.affiliateName || a.affiliateId || '').toLowerCase();
                        const nameB = (b.affiliateName || b.affiliateId || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                      });
                    }
                    
                    return (
                      <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                            {sectionTitle}
                          </Typography>
                          {sectionTotals && (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Total Views: <strong>{sectionTotals.views.toLocaleString()}</strong>
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Sales: <strong>{sectionTotals.sales || 0}</strong>
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Revenue: <strong>${(sectionTotals.revenue || 0).toFixed(2)}</strong>
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox" />
                                {isAdmin && viewAllAffiliates && <TableCell>Affiliate</TableCell>}
                                <TableCell>Blog Title</TableCell>
                                <TableCell align="right">Total Views</TableCell>
                                {SHOW_UNIQUE_SESSIONS && <TableCell align="right">Unique Sessions</TableCell>}
                                <TableCell align="right">Sales</TableCell>
                                <TableCell align="right">Revenue</TableCell>
                                <TableCell align="right">RPV</TableCell>
                                <TableCell align="right">Pages Tracked</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {isAdmin && viewAllAffiliates && affiliateGroups.length > 0 ? (
                                // Render affiliate groups with expandable blogs
                                affiliateGroups.map((affiliateGroup) => {
                                  const affiliateId = affiliateGroup.affiliateId || 'unknown';
                                  const isAffiliateExpanded = expandedAffiliates[affiliateId];
                                  
                                  return (
                                    <React.Fragment key={`affiliate-${affiliateId}`}>
                                      {/* Affiliate Summary Row */}
                                      <TableRow sx={{ backgroundColor: 'grey.50', fontWeight: 'bold' }}>
                                        <TableCell padding="checkbox">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleToggleAffiliate(affiliateId)}
                                          >
                                            {isAffiliateExpanded ? <ExpandLess /> : <ExpandMore />}
                                          </IconButton>
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                          <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                              ID: {affiliateId}
                                            </Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                              {affiliateGroup.affiliateName || affiliateGroup.affiliateId || 'Unknown Affiliate'}
                                            </Typography>
                                            {affiliateGroup.affiliateUsername && (
                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                @{affiliateGroup.affiliateUsername}
                                              </Typography>
                                            )}
                                          </Box>
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                          <Typography variant="body2" fontWeight="bold">
                                            {affiliateGroup.blogs.length} Blog{affiliateGroup.blogs.length !== 1 ? 's' : ''}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <strong>{affiliateGroup.totalViews.toLocaleString()}</strong>
                                        </TableCell>
                                        {SHOW_UNIQUE_SESSIONS && <TableCell align="right">-</TableCell>}
                                        <TableCell align="right">
                                          <strong>{affiliateGroup.totalSales || 0}</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                          <strong>${(affiliateGroup.totalRevenue || 0).toFixed(2)}</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                          <strong>
                                            ${affiliateGroup.totalViews > 0 
                                              ? ((affiliateGroup.totalRevenue || 0) / affiliateGroup.totalViews).toFixed(2)
                                              : '0.00'}
                                          </strong>
                                        </TableCell>
                                        <TableCell align="right">-</TableCell>
                                      </TableRow>
                                      
                                      {/* Blogs for this affiliate */}
                                      {affiliateGroup.blogs.map((blog) => {
                                        const blogPostId = blog.blogPostId?.toString() || blog.blogPostId;
                                        const pageStats = pageStatsArray?.find(b => 
                                          b.blogPostId?.toString() === blogPostId
                                        );
                                        const isBlogExpanded = expandedBlogs[blogPostId];
                                        
                                        return (
                                          <React.Fragment key={`blog-${blogPostId}`}>
                                            <TableRow sx={{ display: isAffiliateExpanded ? 'table-row' : 'none' }}>
                                              <TableCell padding="checkbox"></TableCell>
                                              <TableCell></TableCell>
                                              <TableCell component="th" scope="row">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                  <Box sx={{ flex: 1 }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                      ID: {blogPostId}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                      {blog.blogTitle || blog.blogPostId || 'Unknown Blog'}
                                                    </Typography>
                                                  </Box>
                                                  {pageStats?.pages && pageStats.pages.length > 0 && (
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => handleToggleBlog(blogPostId)}
                                                    >
                                                      {isBlogExpanded ? <ExpandLess /> : <ExpandMore />}
                                                    </IconButton>
                                                  )}
                                                </Box>
                                              </TableCell>
                                              <TableCell align="right">{blog.totalViews.toLocaleString()}</TableCell>
                                              {SHOW_UNIQUE_SESSIONS && (
                                                <TableCell align="right">{blog.uniqueSessions.toLocaleString()}</TableCell>
                                              )}
                                              <TableCell align="right">{blog.sales || 0}</TableCell>
                                              <TableCell align="right">${(blog.totalRevenue || 0).toFixed(2)}</TableCell>
                                              <TableCell align="right">
                                                ${blog.totalViews > 0 
                                                  ? ((blog.totalRevenue || 0) / blog.totalViews).toFixed(2)
                                                  : '0.00'}
                                              </TableCell>
                                              <TableCell align="right">
                                                {pageStats?.pages?.length || 0}
                                              </TableCell>
                                            </TableRow>
                                            
                                            {/* Page breakdown for this blog */}
                                            {pageStats?.pages && pageStats.pages.length > 0 && (
                                              <TableRow sx={{ display: isAffiliateExpanded ? 'table-row' : 'none' }}>
                                                <TableCell 
                                                  style={{ paddingBottom: 0, paddingTop: 0 }} 
                                                  colSpan={SHOW_UNIQUE_SESSIONS ? 10 : 9}
                                                >
                                                  <Collapse in={isBlogExpanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ margin: 2 }}>
                                                      <Typography variant="subtitle2" gutterBottom>
                                                        Page Breakdown:
                                                      </Typography>
                                                      <Table size="small">
                                                        <TableHead>
                                                          <TableRow>
                                                            <TableCell>Page</TableCell>
                                                            <TableCell>Offer ID + Name</TableCell>
                                                            <TableCell>Offer Element ID + Name</TableCell>
                                                            <TableCell align="right">Views</TableCell>
                                                            <TableCell align="right">Clicks</TableCell>
                                                            {SHOW_UNIQUE_SESSIONS && <TableCell align="right">Unique Sessions</TableCell>}
                                                            <TableCell align="right">Sales</TableCell>
                                                            <TableCell align="right">Revenue</TableCell>
                                                            <TableCell align="right">CVR</TableCell>
                                                            <TableCell align="right">RPV</TableCell>
                                                            <TableCell align="right">% of Blog Views</TableCell>
                                                          </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                          {pageStats.pages.map((page, pageIndex) => {
                                                            const isSnake = page.pageNumber === 'Snake' || page.isSnake;
                                                            return (
                                                              <TableRow 
                                                                key={isSnake ? 'snake' : page.pageNumber}
                                                                sx={isSnake ? { 
                                                                  backgroundColor: 'grey.100',
                                                                  fontWeight: 'bold'
                                                                } : {}}
                                                              >
                                                                <TableCell component="th" scope="row">
                                                                  {isSnake ? 'Snake' : `Page ${page.pageNumber}`}
                                                                </TableCell>
                                                                <TableCell>
                                                                  {page.offerId ? (
                                                                    <Box>
                                                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                                        ID: {page.offerId}
                                                                      </Typography>
                                                                      <Typography variant="body2">
                                                                        {page.offerName || 'Unknown Offer'}
                                                                      </Typography>
                                                                    </Box>
                                                                  ) : (
                                                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                      No offer
                                                                    </Typography>
                                                                  )}
                                                                </TableCell>
                                                                <TableCell>
                                                                  {page.offerElementId || page.offerElementName ? (
                                                                    <Box>
                                                                      {page.offerElementId && (
                                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                                          ID: {page.offerElementId}
                                                                        </Typography>
                                                                      )}
                                                                      <Typography variant="body2">
                                                                        {page.offerElementName || 'Unknown Element'}
                                                                      </Typography>
                                                                    </Box>
                                                                  ) : (
                                                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                      No element
                                                                    </Typography>
                                                                  )}
                                                                </TableCell>
                                                                <TableCell align="right">{page.views.toLocaleString()}</TableCell>
                                                                <TableCell align="right">{page.clicks?.toLocaleString() || 0}</TableCell>
                                                                {SHOW_UNIQUE_SESSIONS && (
                                                                  <TableCell align="right">{page.uniqueSessions?.toLocaleString() || 0}</TableCell>
                                                                )}
                                                                <TableCell align="right">{page.sales || 0}</TableCell>
                                                                <TableCell align="right">${(page.totalRevenue || 0).toFixed(2)}</TableCell>
                                                                <TableCell align="right">
                                                                  {page.cvr !== undefined ? `${page.cvr}%` : (page.clicks > 0 ? `${((page.sales || 0) / page.clicks * 100).toFixed(2)}%` : '0.00%')}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                  ${page.views > 0
                                                                    ? ((page.totalRevenue || 0) / page.views).toFixed(2)
                                                                    : '0.00'}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                  {blog.totalViews > 0
                                                                    ? ((page.views / blog.totalViews) * 100).toFixed(1)
                                                                    : 0}%
                                                                </TableCell>
                                                              </TableRow>
                                                            );
                                                          })}
                                                        </TableBody>
                                                      </Table>
                                                    </Box>
                                                  </Collapse>
                                                </TableCell>
                                              </TableRow>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                })
                              ) : (
                                // Render blogs normally (non-admin or single affiliate view)
                                blogsToRender.map((blog) => {
                                  const blogPostId = blog.blogPostId?.toString() || blog.blogPostId;
                                  const pageStats = pageStatsArray?.find(b => 
                                    b.blogPostId?.toString() === blogPostId
                                  );
                                  const isExpanded = expandedBlogs[blogPostId];
                                  
                                  return (
                                    <React.Fragment key={blogPostId}>
                                      <TableRow>
                                        <TableCell padding="checkbox"></TableCell>
                                        <TableCell component="th" scope="row">
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ flex: 1 }}>
                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                ID: {blogPostId}
                                              </Typography>
                                              <Typography variant="body2">
                                                {blog.blogTitle || blog.blogPostId || 'Unknown Blog'}
                                              </Typography>
                                            </Box>
                                            {pageStats?.pages && pageStats.pages.length > 0 && (
                                              <IconButton
                                                size="small"
                                                onClick={() => handleToggleBlog(blogPostId)}
                                              >
                                                {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                              </IconButton>
                                            )}
                                          </Box>
                                        </TableCell>
                                        <TableCell align="right">{blog.totalViews.toLocaleString()}</TableCell>
                                        {SHOW_UNIQUE_SESSIONS && (
                                          <TableCell align="right">{blog.uniqueSessions.toLocaleString()}</TableCell>
                                        )}
                                        <TableCell align="right">{blog.sales || 0}</TableCell>
                                        <TableCell align="right">${(blog.totalRevenue || 0).toFixed(2)}</TableCell>
                                        <TableCell align="right">
                                          ${blog.totalViews > 0 
                                            ? ((blog.totalRevenue || 0) / blog.totalViews).toFixed(2)
                                            : '0.00'}
                                        </TableCell>
                                        <TableCell align="right">
                                          {pageStats?.pages?.length || 0}
                                        </TableCell>
                                      </TableRow>
                                      {pageStats?.pages && pageStats.pages.length > 0 && (
                                        <TableRow>
                                          <TableCell 
                                            style={{ paddingBottom: 0, paddingTop: 0 }} 
                                            colSpan={SHOW_UNIQUE_SESSIONS ? 9 : 8}
                                          >
                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                              <Box sx={{ margin: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                  Page Breakdown:
                                                </Typography>
                                                <Table size="small">
                                                  <TableHead>
                                                    <TableRow>
                                                      <TableCell>Page</TableCell>
                                                      <TableCell>Offer ID + Name</TableCell>
                                                      <TableCell>Offer Element ID + Name</TableCell>
                                                      <TableCell align="right">Views</TableCell>
                                                      <TableCell align="right">Clicks</TableCell>
                                                      {SHOW_UNIQUE_SESSIONS && <TableCell align="right">Unique Sessions</TableCell>}
                                                      <TableCell align="right">Sales</TableCell>
                                                      <TableCell align="right">Revenue</TableCell>
                                                      <TableCell align="right">CVR</TableCell>
                                                      <TableCell align="right">RPV</TableCell>
                                                      <TableCell align="right">% of Blog Views</TableCell>
                                                    </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    {pageStats.pages.map((page, index) => {
                                                      const isSnake = page.pageNumber === 'Snake' || page.isSnake;
                                                      return (
                                                        <TableRow 
                                                          key={isSnake ? 'snake' : page.pageNumber}
                                                          sx={isSnake ? { 
                                                            backgroundColor: 'grey.100',
                                                            fontWeight: 'bold'
                                                          } : {}}
                                                        >
                                                          <TableCell component="th" scope="row">
                                                            {isSnake ? 'Snake' : `Page ${page.pageNumber}`}
                                                          </TableCell>
                                                          <TableCell>
                                                            {page.offerId ? (
                                                              <Box>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                                  ID: {page.offerId}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                  {page.offerName || 'Unknown Offer'}
                                                                </Typography>
                                                              </Box>
                                                            ) : (
                                                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                No offer
                                                              </Typography>
                                                            )}
                                                          </TableCell>
                                                          <TableCell>
                                                            {page.offerElementId || page.offerElementName ? (
                                                              <Box>
                                                                {page.offerElementId && (
                                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                                    ID: {page.offerElementId}
                                                                  </Typography>
                                                                )}
                                                                <Typography variant="body2">
                                                                  {page.offerElementName || 'Unknown Element'}
                                                                </Typography>
                                                              </Box>
                                                            ) : (
                                                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                No element
                                                              </Typography>
                                                            )}
                                                          </TableCell>
                                                          <TableCell align="right">{page.views.toLocaleString()}</TableCell>
                                                          <TableCell align="right">{page.clicks?.toLocaleString() || 0}</TableCell>
                                                          {SHOW_UNIQUE_SESSIONS && (
                                                            <TableCell align="right">{page.uniqueSessions?.toLocaleString() || 0}</TableCell>
                                                          )}
                                                          <TableCell align="right">{page.sales || 0}</TableCell>
                                                          <TableCell align="right">${(page.totalRevenue || 0).toFixed(2)}</TableCell>
                                                          <TableCell align="right">
                                                            {page.cvr !== undefined ? `${page.cvr}%` : (page.clicks > 0 ? `${((page.sales || 0) / page.clicks * 100).toFixed(2)}%` : '0.00%')}
                                                          </TableCell>
                                                          <TableCell align="right">
                                                            ${page.views > 0
                                                              ? ((page.totalRevenue || 0) / page.views).toFixed(2)
                                                              : '0.00'}
                                                          </TableCell>
                                                          <TableCell align="right">
                                                            {blog.totalViews > 0
                                                              ? ((page.views / blog.totalViews) * 100).toFixed(1)
                                                              : 0}%
                                                          </TableCell>
                                                        </TableRow>
                                                      );
                                                    })}
                                                  </TableBody>
                                                </Table>
                                              </Box>
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </React.Fragment>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  };
                  
                  // Use new head/snake structure if available, fallback to old structure
                  const hasHeadSnake = blogStats.head && blogStats.snake;
                  
                  if (hasHeadSnake) {
                    return (
                      <>
                        {/* Head - Individual Blogs + Head Affiliate Pages from Merged Blogs */}
                        {renderBlogTable(
                          blogStats.head.blogs,
                          blogStats.head.pageStats,
                          'Blog Stats',
                          blogStats.head.totals
                        )}
                        
                        {/* Show message if head section is empty */}
                        {(!blogStats.head.blogs || blogStats.head.blogs.length === 0) &&
                         (!blogStats.head.pageStats || blogStats.head.pageStats.length === 0) && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                            No blog views found for this period. Views will appear here once blog posts are viewed.
                          </Typography>
                        )}
                      </>
                    );
                  } else {
                    // Fallback to old structure for backward compatibility
                    return blogStats.byBlog && blogStats.byBlog.length > 0 ? (
                      renderBlogTable(
                        blogStats.byBlog,
                        blogStats.pageStatsByBlog,
                        'Blog Performance by Blog',
                        null
                      )
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                        No blog views found for this period. Views will appear here once blog posts are viewed.
                      </Typography>
                    );
                  }
                })()}

                {/* Page Rankings */}
                {blogStats.overallPageRankings && blogStats.overallPageRankings.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      Top Page Rankings (All Blogs)
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Rank</TableCell>
                            <TableCell>Blog Title</TableCell>
                            <TableCell align="right">Page</TableCell>
                            <TableCell align="right">Views</TableCell>
                            {SHOW_UNIQUE_SESSIONS && <TableCell align="right">Unique Sessions</TableCell>}
                            <TableCell align="right">Sales</TableCell>
                            <TableCell align="right">Revenue</TableCell>
                            <TableCell align="right">RPV</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {blogStats.overallPageRankings.slice(0, 20).map((page, index) => (
                            <TableRow key={`${page.blogPostId}-${page.pageNumber}`}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell component="th" scope="row">
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                    ID: {page.blogPostId}
                                  </Typography>
                                  <Typography variant="body2">
                                    {page.blogTitle || page.blogPostId || 'Unknown Blog'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                Page {page.pageNumber}
                              </TableCell>
                              <TableCell align="right">{page.views.toLocaleString()}</TableCell>
                              {SHOW_UNIQUE_SESSIONS && (
                                <TableCell align="right">{page.uniqueSessions.toLocaleString()}</TableCell>
                              )}
                              <TableCell align="right">{page.sales || 0}</TableCell>
                              <TableCell align="right">${(page.totalRevenue || 0).toFixed(2)}</TableCell>
                              <TableCell align="right">
                                ${page.views > 0
                                  ? ((page.totalRevenue || 0) / page.views).toFixed(2)
                                  : '0.00'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {error ? `Error: ${error}` : 'No blog performance data available. Check the browser console for details.'}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Sub Category Snake Stats Section - Moved to bottom - Admin only */}
        {isAdmin && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6">
                  Sub Category Snake Stats
                </Typography>
                {snakeStats && snakeStats.totals && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Views: <strong>{snakeStats.totals.views.toLocaleString()}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sales: <strong>{snakeStats.totals.sales || 0}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Revenue: <strong>${(snakeStats.totals.revenue || 0).toFixed(2)}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      RPV: <strong>${(snakeStats.totals.rpv || 0).toFixed(2)}</strong>
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {selectedCategory && (
                <Box sx={{ mb: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Category: {categories.find(c => c._id === selectedCategory)?.name || 'N/A'}
                    {selectedSubcategory && ` | Sub Category: ${categories.find(c => c._id === selectedCategory)?.subcategories?.find(s => s._id === selectedSubcategory)?.name || 'N/A'}`}
                  </Typography>
                </Box>
              )}

              {snakeLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : snakeStats && snakeStats.affiliates && snakeStats.affiliates.length > 0 ? (
                (() => {
                  // Restructure data to group by blog first, then affiliate
                  const blogsMap = new Map();
                  
                  snakeStats.affiliates.forEach((affiliate) => {
                    affiliate.blogs.forEach((blog) => {
                      const blogId = blog.blogId?.toString() || blog.blogId;
                      if (!blogsMap.has(blogId)) {
                        blogsMap.set(blogId, {
                          blogId,
                          blogName: blog.blogName,
                          totals: blog.totals,
                          affiliates: []
                        });
                      }
                      
                      const blogEntry = blogsMap.get(blogId);
                      blogEntry.affiliates.push({
                        affiliateId: affiliate.affiliateId,
                        affiliateName: affiliate.affiliateName,
                        username: affiliate.username,
                        offers: blog.offers || []
                      });
                    });
                  });
                  
                  const blogsArray = Array.from(blogsMap.values());
                  
                  return (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox" />
                            <TableCell>Blog</TableCell>
                            <TableCell>Affiliate</TableCell>
                            <TableCell>Username</TableCell>
                            <TableCell>Offer</TableCell>
                            <TableCell align="right">Views</TableCell>
                            <TableCell align="right">Sales</TableCell>
                            <TableCell align="right">Revenue</TableCell>
                            <TableCell align="right">RPV</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {blogsArray.map((blog) => {
                            const blogId = blog.blogId;
                            const snakeBlogKey = `snake-${blogId}`;
                            const isExpanded = expandedBlogs[snakeBlogKey];
                            const hasOffers = blog.affiliates.some(aff => aff.offers && aff.offers.length > 0);
                            // Make merged blogs expandable if they have multiple affiliates (even without offers)
                            const hasMultipleAffiliates = blog.affiliates && blog.affiliates.length > 1;
                            const isExpandable = hasOffers || hasMultipleAffiliates;
                            
                            // Debug logging
                            if (blog.blogName && blog.blogName.includes('Test Merged')) {
                              console.log('[DEBUG] Test Merged Blog:', {
                                blogId,
                                blogName: blog.blogName,
                                affiliatesCount: blog.affiliates?.length || 0,
                                hasOffers,
                                hasMultipleAffiliates,
                                isExpandable,
                                affiliates: blog.affiliates?.map(a => ({ id: a.affiliateId, name: a.affiliateName, offersCount: a.offers?.length || 0 }))
                              });
                            }
                            
                            return (
                              <React.Fragment key={blogId}>
                                {/* Blog totals row */}
                                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                                  <TableCell padding="checkbox">
                                    {isExpandable && (
                                      <IconButton
                                        size="small"
                                        onClick={() => handleToggleBlog(snakeBlogKey)}
                                      >
                                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                      </IconButton>
                                    )}
                                  </TableCell>
                                  <TableCell component="th" scope="row">
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                        ID: {blogId}
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>{blog.blogName}</strong>
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell></TableCell>
                                  <TableCell></TableCell>
                                  <TableCell></TableCell>
                                  <TableCell align="right"><strong>{(blog.totals?.views ?? 0).toLocaleString()}</strong></TableCell>
                                  <TableCell align="right"><strong>{blog.totals?.sales ?? 0}</strong></TableCell>
                                  <TableCell align="right"><strong>${(blog.totals?.revenue ?? 0).toFixed(2)}</strong></TableCell>
                                  <TableCell align="right"><strong>${(blog.totals?.rpv ?? 0).toFixed(2)}</strong></TableCell>
                                </TableRow>
                                {/* Affiliate and Offer rows - Collapsible */}
                                {isExpandable && (
                                  <TableRow>
                                    <TableCell 
                                      style={{ paddingBottom: 0, paddingTop: 0 }} 
                                      colSpan={9}
                                    >
                                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        <Box sx={{ margin: 2 }}>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell>Affiliate</TableCell>
                                                <TableCell>Offer</TableCell>
                                                <TableCell align="right">Views</TableCell>
                                                <TableCell align="right">Sales</TableCell>
                                                <TableCell align="right">Revenue</TableCell>
                                                <TableCell align="right">RPV</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {blog.affiliates.map((affiliate, affiliateIndex) => {
                                                // Show affiliate even if no offers (for merged blogs)
                                                const hasOffersForThisAffiliate = affiliate.offers && affiliate.offers.length > 0;
                                                
                                                if (hasOffersForThisAffiliate) {
                                                  // Show offers for this affiliate
                                                  return affiliate.offers.map((offer, offerIndex) => (
                                                    <TableRow key={`${affiliate.affiliateId}-${offer.offerId}`}>
                                                      <TableCell>
                                                        {offerIndex === 0 && (
                                                          <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                              ID: {affiliate.affiliateId}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                              <strong>{affiliate.affiliateName || 'Unknown'}</strong>
                                                            </Typography>
                                                            {affiliate.username && (
                                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                                @{affiliate.username}
                                                              </Typography>
                                                            )}
                                                            {affiliate.email && (
                                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                                {affiliate.email}
                                                              </Typography>
                                                            )}
                                                          </Box>
                                                        )}
                                                      </TableCell>
                                                      <TableCell>
                                                        <Box>
                                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                            ID: {offer.offerId}
                                                          </Typography>
                                                          <Typography variant="body2">
                                                            {offer.offerName}
                                                          </Typography>
                                                        </Box>
                                                      </TableCell>
                                                      <TableCell align="right">{offer.views.toLocaleString()}</TableCell>
                                                      <TableCell align="right">{offer.sales}</TableCell>
                                                      <TableCell align="right">${offer.revenue.toFixed(2)}</TableCell>
                                                      <TableCell align="right">${offer.rpv.toFixed(2)}</TableCell>
                                                    </TableRow>
                                                  ))
                                                } else {
                                                  // Show affiliate row even without offers (for merged blogs)
                                                  return (
                                                    <TableRow key={`${affiliate.affiliateId}-no-offers`}>
                                                      <TableCell>
                                                        <Box>
                                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                            ID: {affiliate.affiliateId}
                                                          </Typography>
                                                          <Typography variant="body2">
                                                            <strong>{affiliate.affiliateName || 'Unknown'}</strong>
                                                          </Typography>
                                                          {affiliate.username && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                              @{affiliate.username}
                                                            </Typography>
                                                          )}
                                                          {affiliate.email && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                              {affiliate.email}
                                                            </Typography>
                                                          )}
                                                        </Box>
                                                      </TableCell>
                                                      <TableCell>
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                          No offers yet
                                                        </Typography>
                                                      </TableCell>
                                                      <TableCell align="right">0</TableCell>
                                                      <TableCell align="right">0</TableCell>
                                                      <TableCell align="right">$0.00</TableCell>
                                                      <TableCell align="right">$0.00</TableCell>
                                                    </TableRow>
                                                  );
                                                }
                                              })}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  );
                })()
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No snake stats available for the selected filters.
                </Typography>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default ReportsPage;
